import { useState, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { authAPI } from '../api/auth';
import { uploadImage } from '../utils/cloudinary';
import Input from '../components/common/Input';
import Button from '../components/common/Button';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { ShieldCheckIcon } from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Profile = () => {
  const { user, updateUser } = useAuth();
  const [saving, setSaving] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [errors, setErrors] = useState({});
  const [photoError, setPhotoError] = useState('');
  const photoRef = useRef(null);

  const [form, setForm] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: user?.phone || '',
    avatarUrl: user?.avatarUrl || '',
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm(prev => ({ ...prev, [name]: value }));
    setErrors(prev => ({ ...prev, [name]: '' }));
  };

  const handleSave = async () => {
    if (!form.name.trim()) {
      setErrors({ name: 'Name is required' });
      return;
    }

    setSaving(true);
    try {
      const payload = {};
      if (form.name.trim()) payload.name = form.name.trim();
      if (form.email.trim()) payload.email = form.email.trim();
      if (form.phone && form.phone.trim()) payload.phone = form.phone.trim();
      if (form.avatarUrl) payload.avatarUrl = form.avatarUrl;

      const updatedUser = await authAPI.updateProfile(payload);
      updateUser(updatedUser);
      toast.success('Profile updated successfully');
    } catch (error) {
      console.error('Update error:', error);
      toast.error('Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setPhotoError('');
    if (!file.type.startsWith('image/')) { setPhotoError('Please select an image file'); return; }
    if (file.size > 5 * 1024 * 1024) { setPhotoError('Image must be under 5MB'); return; }

    setUploadingPhoto(true);
    try {
      const url = await uploadImage(file);
      const updatedUser = await authAPI.updateProfile({ avatarUrl: url });
      updateUser(updatedUser);
      setForm(prev => ({ ...prev, avatarUrl: url }));
      setPhotoError('');
      toast.success('Profile photo updated');
    } catch (error) {
      console.error('Error uploading photo:', error);
      toast.error('Failed to upload photo');
    } finally {
      setUploadingPhoto(false);
      if (photoRef.current) photoRef.current.value = '';
    }
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--color-background)' }}>
      <div style={{ padding: '32px 32px 32px 24px', maxWidth: '900px' }}>

        {/* Header */}
        <div className="mb-8 text-center">
          <h1 className="text-2xl" style={{ color: 'var(--gray-12)', fontWeight: 700, margin: 0, textAlign: 'center' }}>My Profile</h1>
          <p style={{ color: 'var(--gray-10)', fontSize: '14px', marginTop: '4px', textAlign: 'center' }}>
            Manage your personal information and account settings
          </p>
        </div>

        {/* Account badge */}
        <div
          className="flex items-center gap-3 mb-8 px-4 py-3 rounded-xl"
          style={{
            backgroundColor: 'var(--orange-2)',
            border: '1px solid var(--orange-5)',
          }}
        >
          <ShieldCheckIcon style={{ width: '20px', height: '20px', color: 'var(--orange-9)' }} />
          <span style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)' }}>
            {user?.role?.charAt(0).toUpperCase() + user?.role?.slice(1)} Account — Member since {new Date(user?.createdAt).toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </span>
        </div>

        {/* Content */}
        <div
          className="rounded-xl p-6 md:p-8"
          style={{ backgroundColor: 'var(--gray-2)', border: '1px solid var(--gray-4)' }}
        >
            <div className="space-y-6">
              {/* Profile Photo */}
              <div className="flex items-center gap-5">
                <div style={{ position: 'relative' }}>
                  {form.avatarUrl ? (
                    <div style={{
                      width: '80px', height: '80px', borderRadius: '50%',
                      outline: '3px solid var(--orange-6)', outlineOffset: '2px',
                      overflow: 'hidden', flexShrink: 0,
                    }}>
                      <img
                        src={form.avatarUrl}
                        alt={user?.name}
                        style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                      />
                    </div>
                  ) : (
                    <div
                      style={{
                        width: '80px', height: '80px', borderRadius: '50%',
                        backgroundColor: 'var(--orange-3)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center',
                        fontSize: '28px', fontWeight: 700, color: 'var(--orange-9)',
                        border: '3px solid var(--orange-6)', flexShrink: 0,
                      }}
                    >
                      {user?.name?.charAt(0)?.toUpperCase() || 'U'}
                    </div>
                  )}
                </div>
                <div>
                  <p style={{ fontSize: '15px', fontWeight: 600, color: 'var(--gray-12)', margin: '0 0 6px 0', textAlign: 'left' }}>
                    Profile Photo
                  </p>
                  <input ref={photoRef} type="file" accept="image/*" onChange={handlePhotoUpload} style={{ display: 'none' }} />
                  <button
                    onClick={() => photoRef.current?.click()}
                    disabled={uploadingPhoto}
                    style={{
                      padding: '6px 16px', borderRadius: '8px', border: '1px solid var(--orange-6)',
                      backgroundColor: 'transparent', color: 'var(--orange-9)', fontSize: '13px',
                      fontWeight: 600, cursor: 'pointer',
                    }}
                  >
                    {uploadingPhoto ? 'Uploading...' : form.avatarUrl ? 'Change Photo' : 'Upload Photo'}
                  </button>
                  {photoError && (
                    <p style={{ color: 'var(--red-9, #e5484d)', fontSize: '12px', margin: '6px 0 0', textAlign: 'left' }}>
                      {photoError}
                    </p>
                  )}
                </div>
              </div>

              <Input
                label="Full Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                placeholder="Your full name"
                error={errors.name}
                required
              />

              <Input
                label="Email Address"
                type="email"
                name="email"
                value={form.email}
                onChange={handleChange}
                placeholder="you@example.com"
                required
              />

              <div>
                <label style={{ display: 'block', fontSize: '14px', fontWeight: 500, color: 'var(--gray-12)', marginBottom: '6px', textAlign: 'left' }}>
                  Phone Number
                </label>
                <PhoneInput
                  international
                  countryCallingCodeEditable={false}
                  defaultCountry="UG"
                  value={form.phone}
                  onChange={(value) => setForm(prev => ({ ...prev, phone: value || '' }))}
                  className="phone-input-field"
                />
              </div>
            </div>
        </div>

        {/* Save button */}
        <div className="flex justify-end mt-6">
          <Button onClick={handleSave} loading={saving} size="large">
            Save Changes
          </Button>
        </div>
      </div>
    </div>
  );
};

export default Profile;
